import { collection, addDoc, Timestamp } from 'firebase/firestore';
import React, { useState, useEffect, useRef } from 'react';
/* Added internal dependency import */
import { db } from '../services/firebaseConfig'; 

/**
 * GeminiAssistant Component - Full Platform Intelligence
 * Features: Brand-aligned UI (#0464AD)
 * Data: Students (Docs/Visa), Applications, Applicants, Users, and Program Mapping.
 * Memory: Full chat history support for follow-up questions.
 * Knowledge: Uses dedicated botKnowledge prop for App Script data.
 */
const GeminiAssistant = ({ 
  settings, 
  botKnowledge = "", // New separate prop for App Script data
  students = [], 
  applications = [], 
  applicants = [], 
  users = [], 
  currentUser
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  
  // PERSONALIZED GREETING
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: `Hi ${currentUser?.name || 'there'}! I'm your AI based assistant and can help you program or applicant related information. Please let me know how I can help you today?` 
    }
  ]);
  
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Verification: Ensure this key is active in Google AI Studio
  const apiKey = "AIzaSyB4TvfH7qZxEiaMBPntaj9dh8tu-atp7u0"; 

  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Main AI logic with History Support
   * @param {string} userQuery - The current question
   * @param {Array} history - The previous chat messages
   */
  const fetchGeminiResponse = async (userQuery, history = [], retryCount = 0) => {
    
    // CONSOLE DEBUGGING
    console.log("Assistant Knowledge Check:", botKnowledge ? "BOT DATA FOUND" : "BOT DATA MISSING");

    // Use the dedicated botKnowledge prop for the university catalogue
    const catalogueContext = botKnowledge 
      ? `UNIVERSITY_PROGRAM_CATALOGUE: ${botKnowledge}`
      : "I do not have the specific requirements. Please check the Data Studio report.";

    // Process Checklist Templates from manual settings (Requirements per country)
    const checklistTemplates = settings.documentTemplates ? JSON.stringify(settings.documentTemplates) : 'No master templates defined.';

    // Enhanced Student Context
    const studentContext = students.map(s => {
      const docEntries = s.documentChecklist ? Object.entries(s.documentChecklist) : [];
      const docStatus = docEntries.length > 0 
        ? docEntries.map(([docName, data]) => `${docName}: ${data.status}`).join(', ') 
        : 'No documents started';
      
      return `STUDENT: ${s.name} | Stage: ${s.stage} | Uni: ${s.destinationUniversity} | Country: ${s.targetCountry} | Docs: [${docStatus}] | ID: ${s.id}`;
    }).join('\n');

    const appContext = applications.map(a => 
      `APPLICATION: ${a.applicantName} | Status: ${a.status} | Agent: ${a.agent} | Date Applied: ${a.dateApplied || 'N/A'} | ID: ${a.id}`
    ).join('\n');

    const applicantContext = applicants.map(ap => 
      `LEAD: ${ap.firstName} ${ap.lastName} | Email: ${ap.email} | Nationality: ${ap.citizenship}`
    ).join('\n');

    const userContext = users.map(u => 
      `TEAM: ${u.name} | Role: ${u.role} | Status: ${u.status}`
    ).join('\n');

    // 2. SYSTEM INSTRUCTIONS
    const systemPrompt = `
      You are ${currentUser?.name || 'User'}'s Support Assistant for the WorldGrad platform. 
      TODAY'S DATE: ${today}.

      <DATABASE_CONTEXT>
      ${catalogueContext}

      MASTER CHECKLIST REQUIREMENTS (By Country):
      ${checklistTemplates}

      STUDENT PIPELINE & DOCUMENT STATUS:
      ${studentContext || 'No students in pipeline.'}
      
      ACTIVE_APPLICATIONS:
      ${appContext || 'No applications found.'}
      
      APPLICANT_LEADS:
      ${applicantContext || 'No applicant leads found.'}
      
      TEAM_DIRECTORY:
      ${userContext || 'No users registered.'}
      </DATABASE_CONTEXT>

      VISA_READINESS_LOGIC:
      1. To determine if a student is "Ready for Visa Filing":
          - Look up their "Country" in MASTER CHECKLIST REQUIREMENTS.
          - Check their "Docs" in STUDENT PIPELINE.
          - If EVERY required document for that country is marked as "Complete", they are READY.
          - If any required document is "Pending", they are NOT READY.
      
      PROGRAM_INFO_LOGIC:
      1. Use the UNIVERSITY_PROGRAM_CATALOGUE to answer specific questions about university GPA, fees, IELTS, and requirements.
      2. If a specific university is not in the detailed catalogue, check the core UNIVERSITIES list in the platform settings.

      GENERAL_INSTRUCTIONS:
      1. You are talking to ${currentUser?.name || 'the user'}. Be professional, direct, and data-driven.
      2. Cross-reference: Link leads to applications if names match.
      3. Use Today's Date for relative time queries.
    `.trim();

    // 3. FORMAT HISTORY FOR GEMINI (Role: user/model)
    const formattedHistory = history.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }]
    }));

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_instruction: { parts: [{ text: systemPrompt }] },
          contents: [
            ...formattedHistory,
            { role: "user", parts: [{ text: userQuery }] }
          ],
          generationConfig: { 
            temperature: 0.1, 
            maxOutputTokens: 1500 
          }
        })
      });

      if (!response.ok) throw new Error("Connection failed");
      const result = await response.json();
      return result.candidates?.[0]?.content?.parts?.[0]?.text || "I'm sorry, I couldn't process that.";
    } catch (error) {
      if (retryCount < 2) { 
        await new Promise(res => setTimeout(res, 2000));
        return fetchGeminiResponse(userQuery, history, retryCount + 1);
      }
      return "I'm having trouble connecting to the platform data. Please try again.";
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    /* MODIFIED: Capture the user query text for logging */
    const userQueryText = input; 
    const userMsg = { role: 'user', content: userQueryText };
    const currentHistory = [...messages]; // Capture history before state update
    
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    
    // Pass history to the fetch function
    const botResponse = await fetchGeminiResponse(userQueryText, currentHistory);

    /* NEW: LOG INTERACTION TO FIRESTORE */
    try {
      await addDoc(collection(db, 'artifacts', 'worldgrad-crm', 'public', 'data', 'bot_logs'), {
        userId: currentUser?.uid || 'Unknown',
        userName: currentUser?.name || 'Unknown User',
        userEmail: currentUser?.email || 'N/A',
        userRole: currentUser?.role || 'N/A',
        query: userQueryText,
        response: botResponse,
        timestamp: Timestamp.now()
      });
    } catch (error) {
      console.error("Failed to log bot interaction:", error);
    }
    
    setMessages(prev => [...prev, { role: 'assistant', content: botResponse }]);
    setIsLoading(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] font-sans">
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-[420px] h-[600px] bg-white rounded-[2.5rem] shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-6 duration-300">
          <div className="bg-[#0464AD] p-6 text-white">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl font-bold">?</div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest leading-none">Support Assistant</h3>
                  <p className="text-[10px] text-blue-100 font-medium mt-1">Unified Platform Intelligence</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-2 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user' ? 'bg-[#0464AD] text-white shadow-md' : 'bg-white text-slate-700 border border-slate-200 shadow-sm'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start px-2">
                <div className="bg-white px-4 py-3 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <input 
                type="text" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about students, visa, or uni requirements..."
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]"
              />
              <button onClick={handleSend} disabled={isLoading} className="w-12 h-12 bg-slate-900 text-white rounded-xl flex items-center justify-center hover:bg-slate-800 active:scale-95 disabled:opacity-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 19l9-2-9-18-9 18 9 2zm0 0v-8"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className={`w-16 h-16 rounded-3xl shadow-2xl flex items-center justify-center transition-all duration-500 hover:scale-110 active:scale-95 ${
          isOpen ? 'bg-slate-900' : 'bg-[#0464AD]'
        }`}
      >
        {isOpen ? (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
        ) : (
          <span className="text-2xl font-bold text-white">?</span>
        )}
      </button>
    </div>
  );
};

export default GeminiAssistant;